import { EventPhoto, type EventPhotoList } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { EventClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type EventPhotoFilter = EntityFilter<EventPhoto>;

export const useEventPhotoBatchGetQuery = <TSelectData = EventPhotoList,>({
  enabled = true,
  filter = {},
  select,
}: {
  enabled?: boolean;
  filter?: EventPhotoFilter;
  select?: (data: EventPhotoList) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: () => {
      const request = EventPhoto.create(filter);
      return EventClientService.BatchGetEventPhoto(request);
    },
    queryKey: [
      queryKeys.eventPhoto.root,
      queryKeys.eventPhoto.list,
      queryKeys.eventPhoto.getHash(filter),
    ],
    enabled,
    select,
  });
};

export const useEventPhotoGetQuery = ({
  enabled = true,
  id,
}: {
  enabled?: boolean;
  id: number;
}) => {
  return useQuery({
    queryFn: () => {
      const request = EventPhoto.create({ id });
      return EventClientService.GetEventPhoto(request);
    },
    queryKey: [queryKeys.eventPhoto.root, id],
    enabled: enabled && !!id,
  });
};

export const useCreateEventPhotoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventPhoto: EventPhoto) => {
      return await EventClientService.CreateEventPhoto(eventPhoto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.eventPhoto.root],
      });
    },
  });
};

export const useUpdateEventPhotoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventPhoto: EventPhoto) => {
      return await EventClientService.UpdateEventPhoto(eventPhoto);
    },
    onSuccess: (updatedPhoto, variables) => {
      if (updatedPhoto) {
        // Check if this is a soft-delete (isActive=false or include flags set to false)
        const isSoftDelete =
          updatedPhoto.isActive === false ||
          variables.includeInProposal === false ||
          variables.includeInInvoice === false;

        // Update all event photo list queries
        queryClient.setQueriesData<EventPhotoList>(
          { queryKey: [queryKeys.eventPhoto.root, queryKeys.eventPhoto.list] },
          (cache) => {
            if (!cache) return cache;

            if (isSoftDelete) {
              // Remove the photo from the results if it's soft-deleted
              const results = cache.results.filter((photo) => photo.id !== updatedPhoto.id);
              return {
                ...cache,
                results,
                totalCount: Math.max(0, (cache.totalCount || cache.results.length) - 1),
              };
            } else {
              // Update the photo in the results
              const results = cache.results.map((photo) => {
                if (photo.id === updatedPhoto.id) {
                  return updatedPhoto;
                }
                return photo;
              });

              return {
                ...cache,
                results,
              };
            }
          },
        );
      }
    },
  });
};

export const useDeleteEventPhotoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventPhoto: EventPhoto) => {
      return await EventClientService.DeleteEventPhoto(eventPhoto);
    },
    onSuccess: (_, variables) => {
      queryClient.setQueriesData<EventPhotoList>(
        { queryKey: [queryKeys.eventPhoto.root, queryKeys.eventPhoto.list] },
        (cache) => {
          if (cache) {
            const results = cache.results.filter((photo) => photo.id !== variables.id);
            return {
              ...cache,
              totalCount: cache.totalCount - 1,
              results,
            };
          }
          return cache;
        },
      );
    },
  });
};
